import React, { ReactNode, useEffect, useRef, useState } from 'react';

import { useDrag, useRefresh } from 'muuri-react';

import { ResizableBox } from 'react-resizable';
import { useUpdateChartConfigMutation } from '@api/chart';
import { ChartConfigType, VisualSettings } from '@DStypes/chart';
import { useDispatch } from 'react-redux';
import { updateConfigs } from '@pages/ProjectBoard/slice';
import { useDebouncedCallback } from 'use-debounce';
import { sendAmplitudeData } from '@utils/amplitude';
import { RESIZED_CHART } from '@constants/eventNames';

interface ResizableWrapperProps {
  width: number;
  height: number;
  minConstraints: any;
  maxConstraints: any;
  unitWidth: number;
  unitHeight: number;
  visualSettings?: VisualSettings | null;
  chartId: string;
  openConfigurator: boolean;
  miniSidenav: boolean;
  charts: ChartConfigType[];
  setCharts: React.Dispatch<React.SetStateAction<ChartConfigType[]>>;
  chartConfigs: any[];
  setForceUpdate: any;
  forceUpdate: any;
  children: ReactNode;
  isKpi: boolean;
}

export const ResizableWrapper: React.FC<ResizableWrapperProps> = ({
  height,
  width,
  minConstraints,
  maxConstraints,
  unitHeight,
  unitWidth,
  visualSettings,
  chartId,
  openConfigurator,
  miniSidenav,
  charts,
  setCharts,
  chartConfigs,
  setForceUpdate,
  forceUpdate,
  isKpi,
  children
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const [resizeTimeout, setResizeTimeout] = useState<any>(null);

  const isDragging = useDrag();

  const refresh = useRefresh();

  //To refresh the chart cards so that they rearrange in the chart container optimally on side nav open/close,configurator open/close

  useEffect(() => {
    setTimeout(() => {
      refresh();
    }, 600);
  }, [openConfigurator, miniSidenav, charts]);

  const [updateChartConfigWithoutRerender] = useUpdateChartConfigMutation();

  const refreshWithdebounce = useDebouncedCallback(
    () => requestAnimationFrame(refresh),
    10
  );

  const handleResize = (_: any, { size }: any) => {
    if (resizeTimeout) clearTimeout(resizeTimeout);

    const newTimeout = setTimeout(() => {
      if (ref.current) {
        const widthFactor = Math.round(size.width / unitWidth);
        const heightFactor = Math.round(size.height / unitHeight);

        const roundedWidth = unitWidth * widthFactor;

        const roundedHeight = unitHeight * heightFactor;

        const currentChart = chartConfigs.find((chart) => chart.id === chartId);

        if (
          (currentChart.config.visual_settings?.size?.w || (isKpi ? 1 : 2)) ===
            widthFactor &&
          (currentChart.config.visual_settings?.size?.h || (isKpi ? 1 : 2)) ===
            heightFactor
        ) {
          ref.current.style.width = `${roundedWidth}px`;
          ref.current.style.height = `${roundedHeight}px`;
          const newForceUpdate = forceUpdate.map((obj: any) => {
            if (obj.id === chartId)
              return {
                ...obj,
                update: !obj.update
              };

            return obj;
          });

          setForceUpdate(newForceUpdate);
        } else {
          ref.current.style.width = `${roundedWidth}px`;
          ref.current.style.height = `${roundedHeight}px`;

          const newSize = {
            h: heightFactor,
            w: widthFactor
          };

          sendAmplitudeData(RESIZED_CHART, {
            'Size (before)': `${visualSettings?.size?.w || (isKpi ? 1 : 2)}x${
              visualSettings?.size?.h || (isKpi ? 1 : 2)
            }`,
            'Size (after)': `${newSize.w}x${newSize.h}`
          });

          const newLoadedCharts = charts.map((obj) => {
            if (obj.id === chartId)
              // Create a new object with the updated size property
              return {
                ...obj,
                config: {
                  ...obj.config,
                  visual_settings: {
                    ...visualSettings,
                    size: newSize
                  }
                }
              };

            return obj;
          });

          const newChartConfigs = chartConfigs.map((obj) => {
            if (obj.id === chartId)
              // Create a new object with the updated size property
              return {
                ...obj,
                config: {
                  ...obj.config,
                  visual_settings: {
                    ...visualSettings,
                    size: newSize
                  }
                }
              };

            return obj;
          });

          setCharts(newLoadedCharts);
          dispatch(updateConfigs(newChartConfigs));
          updateChartConfigWithoutRerender({
            id: chartId,
            payload: {
              field: 'visual_settings',
              value: {
                ...visualSettings,
                size: newSize
              }
            }
          });
        }
      }

      refreshWithdebounce();
    }, 500);

    setResizeTimeout(newTimeout);
  };

  return (
    <div
      ref={ref}
      style={{
        width: `${width}px`,
        height: `${height}px`,
        marginRight: '7px',
        marginBottom: '7px',
        zIndex: isDragging ? 20 : 10
      }}
    >
      <ResizableBox
        resizeHandles={['nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w']}
        width={width}
        height={height}
        minConstraints={minConstraints}
        maxConstraints={maxConstraints}
        onResizeStop={handleResize}
        onResize={(_, { size }) => {
          if (ref.current) {
            ref.current.style.width = size.width + 'px';
            ref.current.style.height = size.height + 'px';
          }

          refreshWithdebounce();
        }}
      >
        {children}
      </ResizableBox>
    </div>
  );
};
